import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { LOTTO_CONSTANTS } from '../constants';
import {
  parseStakeInput,
  calculateSafeStake,
  formatStakeDisplay,
} from '../utils/stakeUtils';

export const useStakeInput = ({
  initialStake,
  minStake,
  maxStake,
  onChange,
  onBlur,
}) => {
  const config = useMemo(
    () => ({
      minStake,
      maxStake,
      defaultStake: LOTTO_CONSTANTS.DEFAULT_STAKE,
    }),
    [minStake, maxStake]
  );

  const [stake, setStake] = useState(initialStake);
  const [stakeInput, setStakeInput] = useState(
    initialStake > 0 ? formatStakeDisplay(initialStake) : String(initialStake)
  );
  const [isEditing, setIsEditing] = useState(false);
  const [isDirty, setIsDirty] = useState(false);

  const isInitializedRef = useRef(initialStake > 0);

  useEffect(() => {
    if (!isInitializedRef.current && initialStake > 0) {
      isInitializedRef.current = true;
      setStake(initialStake);
      setStakeInput(formatStakeDisplay(initialStake));
    }
  }, [initialStake]);

  const isValid = maxStake >= minStake;

  const handleChange = useCallback(
    (value) => {
      setIsDirty(true);
      const strippedValue = value.replace(/,/g, '');
      const { sanitizedInput, numericValue } = parseStakeInput(strippedValue);
      setStakeInput(sanitizedInput);

      if (numericValue === null) return;

      let newStake;
      if (numericValue > maxStake) {
        const clamped = Math.max(maxStake, minStake);
        setStake(clamped);
        setStakeInput(clamped.toString());
        newStake = clamped;
      } else if (numericValue < minStake) {
        setStake(minStake);
        newStake = minStake;
      } else {
        setStake(numericValue);
        newStake = numericValue;
      }

      onChange?.(newStake);
    },
    [maxStake, minStake, onChange]
  );

  const stopEditing = useCallback(() => {
    if (!isDirty) {
      setStake(initialStake);
      setStakeInput(formatStakeDisplay(initialStake));
      setIsEditing(false);
      return;
    }
    const safeValue = calculateSafeStake(stakeInput.replace(/,/g, ''), config);
    setStake(safeValue);
    setStakeInput(formatStakeDisplay(safeValue));
    setIsEditing(false);
    onBlur?.(safeValue);
  }, [stakeInput, config, onBlur, isDirty, initialStake]);

  const startEditing = useCallback(() => {
    setIsDirty(false);
    setIsEditing(true);
  }, []);

  const toggleEditing = useCallback(() => {
    if (isEditing) stopEditing();
    else startEditing();
  }, [isEditing, stopEditing, startEditing]);

  const displayValue = formatStakeDisplay(stake);

  return {
    stake,
    stakeInput,
    isEditing,
    isValid,
    startEditing,
    stopEditing,
    toggleEditing,
    handleChange,
    displayValue,
  };
};
